// Dashboard.tsx (updated for nested routing but preserving original layout)

import { useEffect, useRef } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDataContext } from "../context/DataContext";
import { useDevFeatures } from "../context/DevFeaturesContext";
import UserBadge from "../components/UserBadge";

export default function Dashboard() {
  const { checkAndRefreshIfStale, hasTimesheets } = useDataContext();
  const { endpointsPageAvailable } = useDevFeatures();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Track the last non-settings path for return navigation
  const returnPathRef = useRef<string>("/app");
  const isOnSettings = location.pathname === "/app/settings";
  
  // Update return path when navigating to a non-settings page
  useEffect(() => {
    if (!isOnSettings) {
      returnPathRef.current = location.pathname;
    }
  }, [location.pathname, isOnSettings]);
  
  // Handle settings button click
  const handleSettingsClick = () => {
    if (isOnSettings) {
      // Already on settings, go back to previous page
      navigate(returnPathRef.current);
    } else {
      // Not on settings, go to settings
      navigate("/app/settings", { state: { returnTo: location.pathname } });
    }
  };

  // Check for stale data on route change
  useEffect(() => {
    checkAndRefreshIfStale();
  }, [location.pathname, checkAndRefreshIfStale]);

  return (
    <div className={`app-container${isOnSettings ? " app-container--settings" : ""}`}>
      <header className="top-banner">
        <UserBadge />
        <div className="top-banner__actions">
          <button
            type="button"
            onClick={handleSettingsClick}
            className={`top-banner__btn${isOnSettings ? " active" : ""}`}
            title="Settings"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            <span>Settings</span>
          </button>
        </div>
      </header>

      <nav className={`mobile-top-bar${isOnSettings ? " mobile-top-bar--title" : ""}`}>
        {isOnSettings ? (
          <>
            <button
              type="button"
              onClick={() => navigate(returnPathRef.current)}
              className="mobile-top-bar__btn"
              aria-label="Back"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <span className="mobile-top-bar__title">Settings</span>
          </>
        ) : (
          <>
            <UserBadge />
            <button
              type="button"
              onClick={handleSettingsClick}
              className="mobile-top-bar__btn"
              title="Settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>
          </>
        )}
      </nav>

      {/* Mobile bottom navigation - only visible on small screens */}
      <nav className="mobile-bottom-nav">
        <NavLink
          to="/app/dashboard"
          className={({ isActive }) => (isActive ? "active" : undefined)}
          title="Dashboard"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span className="mobile-bottom-nav__label">Home</span>
        </NavLink>
        <NavLink
          to="/app/schedule"
          className={({ isActive }) => (isActive ? "active" : undefined)}
          title="Schedule"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <span className="mobile-bottom-nav__label">Schedule</span>
        </NavLink>
        <NavLink
          to="/app/jobs"
          className={({ isActive }) => (isActive ? "active" : undefined)}
          title="Jobs"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
          </svg>
          <span className="mobile-bottom-nav__label">Jobs</span>
        </NavLink>
        <NavLink
          to="/app/competencies"
          className={({ isActive }) => (isActive ? "active" : undefined)}
          title="Competencies"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span className="mobile-bottom-nav__label">Skills</span>
        </NavLink>
        <NavLink
          to="/app/travel"
          className={({ isActive }) => (isActive ? "active" : undefined)}
          title="Travel Bookings"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z"/>
          </svg>
          <span className="mobile-bottom-nav__label">Travel</span>
        </NavLink>
        {hasTimesheets && (
          <NavLink
            to="/app/timesheets"
            className={({ isActive }) => (isActive ? "active" : undefined)}
            title="Timesheets"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
              <circle cx="12" cy="14" r="4.5"></circle>
              <polyline points="12 11.5 12 14 13.75 15.25"></polyline>
            </svg>
            <span className="mobile-bottom-nav__label">Timesheets</span>
          </NavLink>
        )}
      </nav>

      <div className="layout">
        
        {/* Desktop navigation */}
        <aside className="sidebar">
          <NavLink
            to="/app/dashboard"
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            Dashboard
          </NavLink>

          <NavLink
            to="/app/schedule"
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            Schedule
          </NavLink>

          <NavLink
            to="/app/jobs"
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            Jobs
          </NavLink>

          <NavLink
            to="/app/competencies"
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            Competencies
          </NavLink>

          <NavLink
            to="/app/travel"
            className={({ isActive }) => (isActive ? "active" : undefined)}
          >
            Travel Bookings
          </NavLink>

          {hasTimesheets && (
            <NavLink
              to="/app/timesheets"
              className={({ isActive }) => (isActive ? "active" : undefined)}
            >
              Timesheets
            </NavLink>
          )}

          <div className="sidebar__spacer" />

          {endpointsPageAvailable && (
            <NavLink
              to="/app/endpoints"
              className={({ isActive }) => `sidebar__debug${isActive ? " active" : ""}`}
            >
              Endpoints (Debugging)
            </NavLink>
          )}
        </aside>

        {/* Main content area where child routes render */}
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
